// src/components/admin/sales/AdminUsersNew.tsx
// Gestão de Usuários - IDENTIDADE GRAIA

import React, { useState, useEffect } from 'react';
import { Search, X, Filter, Trash2, Users } from 'lucide-react';
import './AdminUsersNew.css';
import { useAdminUsers } from '../../hooks/useAdminQueries';
import { useQueryClient } from '@tanstack/react-query';
import UserSessionsButton from './UserSessionsButton';

const AdminUsersNew: React.FC = () => {


    // Filtros ativos
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUniversity, setSelectedUniversity] = useState('');
    const [selectedCourse, setSelectedCourse] = useState('');
    const [selectedSemester, setSelectedSemester] = useState('');
    const [selectedAuthProvider, setSelectedAuthProvider] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const qc = useQueryClient();

    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const { data, isLoading } = useAdminUsers({
        search: debouncedSearch,
        university: selectedUniversity,
        course: selectedCourse,
        semester: selectedSemester,
        authProvider: selectedAuthProvider,
    });
    const users = data?.users ?? [];
    const filters = data?.filters ?? { universities: [], courses: [], semesters: [], goals: [], authProviders: [] };
    const loading = isLoading && users.length === 0;


    const handleDeleteUser = async (userId: string, username: string) => {
        if (!window.confirm(`Deseja realmente remover o usuário "${username}"?`)) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`/api/admin/users/${userId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {

                qc.invalidateQueries({ queryKey: ['adminUsers'] });
            } else {
                const data = await response.json();
                alert(data.message || 'Erro ao remover usuário');
            }
        } catch (error) {
            console.error('Erro ao deletar usuário:', error);
            alert('Erro ao remover usuário');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedUniversity('');
        setSelectedCourse('');
        setSelectedSemester('');
        setSelectedAuthProvider('');
    };

    const hasActiveFilters =
        searchTerm ||
        selectedUniversity ||
        selectedCourse ||
        selectedSemester ||
        selectedAuthProvider;

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Não informado';
        // Pega só a parte da data YYYY-MM-DD e formata sem converter timezone
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
    };

    const getAge = (birthDate: string | null) => {
        if (!birthDate) return null;
        const birth = new Date(birthDate);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const getRoleBadge = (role: string) => {
        const roleMap: Record<string, { label: string; class: string }> = {
            admin: { label: 'Admin', class: 'role-admin' },
            vendor: { label: 'Vendedor', class: 'role-vendor' },
            user: { label: 'Usuário', class: 'role-user' }
        };
        const r = roleMap[role] || roleMap.user;
        return <span className={`adm-usr-role-badge ${r.class}`}>{r.label}</span>;
    };

    const getAuthBadge = (provider: string) => {
        const providerMap: Record<string, string> = {
            local: 'Email',
            google: 'Google',
            'local+google': 'Email + Google'
        };
        return providerMap[provider] || provider;
    };

    if (loading && users.length === 0) {
        return (
            <div className="adm-usr-container adm-page">
                <div className="adm-usr-loading">
                    <div className="adm-usr-spinner"></div>
                    <p>Carregando usuários</p>
                </div>
            </div>
        );
    }

    return (
        <div className="adm-usr-container adm-page">
            <div className="adm-page-header">
                <h1 className="adm-page-title">Gestão de Usuários</h1>
                <p className="adm-page-subtitle">
                    {users.length} {users.length === 1 ? 'usuário encontrado' : 'usuários encontrados'}
                </p>
            </div>

            {/* Barra de Busca e Filtros */}
            <div className="adm-usr-search-filter">
                <div className="adm-usr-search-bar">
                    <Search size={18} className="adm-usr-search-icon" />
                    <input
                        type="text"
                        placeholder="Buscar por nome ou email"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="adm-usr-search-clear" onClick={() => setSearchTerm('')}>
                            <X size={18} />
                        </button>
                    )}
                </div>

                <button
                    className={`adm-usr-btn-toggle-filters ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <Filter size={18} />
                    Filtros
                    {hasActiveFilters && <span className="adm-usr-filters-badge"></span>}
                </button>
            </div>

            {/* Painel de Filtros Avançados */}
            {showFilters && (
                <div className="adm-usr-filters-panel">
                    <div className="adm-usr-filters-grid">
                        <div className="adm-usr-filter-group">
                            <label>Universidade</label>
                            <select
                                value={selectedUniversity}
                                onChange={(e) => setSelectedUniversity(e.target.value)}
                            >
                                <option value="">Todas</option>
                                <option value="null">Não declarado</option>
                                {filters.universities.map((uni: string) => (
                                    <option key={uni} value={uni}>{uni}</option>
                                ))}
                            </select>
                        </div>

                        <div className="adm-usr-filter-group">
                            <label>Curso</label>
                            <select
                                value={selectedCourse}
                                onChange={(e) => setSelectedCourse(e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="null">Não declarado</option>
                                {filters.courses.map((course: string) => (
                                    <option key={course} value={course}>{course}</option>
                                ))}
                            </select>
                        </div>

                        <div className="adm-usr-filter-group">
                            <label>Semestre</label>
                            <select
                                value={selectedSemester}
                                onChange={(e) => setSelectedSemester(e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="null">Não declarado</option>
                                {filters.semesters.map((sem: string) => (
                                    <option key={sem} value={sem}>{sem}</option>
                                ))}
                            </select>
                        </div>

                        <div className="adm-usr-filter-group">
                            <label>Método de Login</label>
                            <select
                                value={selectedAuthProvider}
                                onChange={(e) => setSelectedAuthProvider(e.target.value)}
                            >
                                <option value="">Todos</option>
                                {filters.authProviders.map((provider: string) => (
                                    <option key={provider} value={provider}>
                                        {getAuthBadge(provider)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {hasActiveFilters && (
                        <button className="adm-usr-btn-clear-filters" onClick={clearFilters}>
                            Limpar Filtros
                        </button>
                    )}
                </div>
            )}

            {/* Tabela de Usuários */}
            {users.length === 0 ? (
                <div className="adm-usr-empty">
                    <Users size={48} />
                    <p>Nenhum usuário encontrado</p>
                    {hasActiveFilters && (
                        <button className="adm-usr-btn-clear-search" onClick={clearFilters}>
                            Limpar Filtros
                        </button>
                    )}
                </div>
            ) : (
                <div className="adm-usr-table-container">
                    <table className="adm-usr-table">
                        <thead>
                            <tr>
                                <th>Usuário</th>
                                <th>Perfil Acadêmico</th>
                                <th>Informações</th>
                                <th>Acesso</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user: any) => {
                                const age = getAge(user.birthDate);
                                return (
                                    <tr key={user._id}>
                                        <td>
                                            <div className="adm-usr-user-cell">
                                                {user.profilePhoto ? (
                                                    <img
                                                        src={user.profilePhoto}
                                                        alt={user.username}
                                                        className="adm-usr-avatar-photo"
                                                    />
                                                ) : (
                                                    <div className="adm-usr-avatar">
                                                        {user.username.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div className="adm-usr-user-info">
                                                    <div className="adm-usr-user-name" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                                                        {user.username}
                                                        <UserSessionsButton userId={user._id} username={user.username} />
                                                    </div>
                                                    <div className="adm-usr-user-email">{user.email}</div>
                                                    {getRoleBadge(user.role)}
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="adm-usr-academic">
                                                {user.university && <div><strong>Univ:</strong> {user.university}</div>}
                                                {user.course && <div><strong>Curso:</strong> {user.course}</div>}
                                                {user.semester && <div><strong>Sem:</strong> {user.semester}</div>}
                                                {!user.university && !user.course && !user.semester && (
                                                    <span className="adm-usr-muted">Não preenchido</span>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="adm-usr-info">
                                                <div><strong>Celular:</strong> {user.phone || <span className="adm-usr-muted">Não informado</span>}</div>
                                                <div><strong>Nascimento:</strong> {age ? `${formatDate(user.birthDate)} (${age} anos)` : <span className="adm-usr-muted">Não informado</span>}</div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="adm-usr-access">
                                                <div>{getAuthBadge(user.authProvider)}</div>
                                                <div className="adm-usr-muted">Desde {formatDate(user.createdAt)}</div>
                                            </div>
                                        </td>

                                        <td>
                                            {user.role !== 'admin' && (
                                                <button
                                                    className="adm-usr-btn-delete"
                                                    onClick={() => handleDeleteUser(user._id, user.username)}
                                                    title="Remover usuário"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminUsersNew;